import type { SortOrder } from "antd/es/table/interface";
import { useIntl } from "react-intl";
import styled, { css } from "styled-components";
import StandingsTableActions from "~pages/Standings/components/StandingsTableActions";
import { StandingColumns } from "~pages/Standings/enums/standingColumns";
import type { StandingTransformed } from "~pages/Standings/types/standingTransformed";
import type { StandingsCalculated, StandingsPoints } from "~types/standing";

interface ResultSpanProps {
  result: string;
}

const ResultSpan = styled.div<ResultSpanProps>`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  ${({ result }) => css`
    background-color: ${result === "W" ? "#0cb50c" : result === "L" ? "#b50c0c" : "#ffffff"};
    border: 1px solid ${result === "W" ? "#0cb50c" : result === "L" ? "#b50c0c" : "#000000"};
  `}
`;

const roundNumber = (number?: number | null) => (number ? Math.round((number + Number.EPSILON) * 100) / 100 : 0);

const composeResultString = (resultString?: string) => (
  <div style={{ display: "flex", gap: 2 }}>
    {resultString
      ?.split("")
      .slice(-5)
      .map((r, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: result items don't have a unique id
        <ResultSpan key={index} result={r.toUpperCase()} />
      ))}
  </div>
);

export const useGetColumns = () => {
  const { formatMessage } = useIntl();

  const columns = {
    [StandingColumns.RowNumber]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.RowNumber}`,
      }),
      dataIndex: "position",
      width: 50,
      fixed: "left",
      sorter: (a: StandingTransformed, b: StandingTransformed) => a.position - b.position,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["descend", "ascend", "descend"] as SortOrder[],
    },
    [StandingColumns.Teamname]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.Teamname}`,
      }),
      dataIndex: "entityId",
      fixed: "left",
      render: (_: unknown, row: StandingTransformed) => (
        <div>
          {row?.entity?.images?.[0]?.url && <img style={{ width: 25 }} src={row?.entity?.images?.[0]?.url} alt="&nbsp;(team-logo)&nbsp;" />}
          {row?.entity?.nameFullLocal}
        </div>
      ),
    },
    [StandingColumns.Played]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.Played}`,
      }),
      dataIndex: "calculated",
      align: "center",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.played}</div>,
    },
    [StandingColumns.Wins]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Wins}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.wins}</div>,
      align: "center",
    },
    [StandingColumns.WinsAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.WinsAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.winsAway}</div>,
      align: "center",
    },
    [StandingColumns.WinsHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.WinsHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.winsHome}</div>,
      align: "center",
    },
    [StandingColumns.Losses]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.Losses}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.losses}</div>,
      align: "center",
    },
    [StandingColumns.Draws]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Draws}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.draws}</div>,
      align: "center",
    },
    [StandingColumns.DrawsAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.DrawsAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.drawsAway}</div>,
      align: "center",
    },
    [StandingColumns.DrawsHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.DrawsHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.drawsHome}</div>,
      align: "center",
    },
    [StandingColumns.Byes]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Byes}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.byes}</div>,
      align: "center",
    },
    [StandingColumns.Washouts]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.Washouts}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.washouts}</div>,
      align: "center",
    },
    [StandingColumns.WinPercentage]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.WinPercentage}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{roundNumber(value?.OVERALL?.winPercentage * 100)}</div>,
      align: "center",
    },
    [StandingColumns.WinPercentageAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.WinPercentageAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{roundNumber(value?.OVERALL?.winPercentageAway * 100)}</div>,
      align: "center",
    },
    [StandingColumns.WinPercentageHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.WinPercentageHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{roundNumber(value?.OVERALL?.winPercentageHome * 100)}</div>,
      align: "center",
    },
    [StandingColumns.ScoredFor]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ScoredFor}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.scoredFor}</div>,
      align: "center",
    },
    [StandingColumns.ScoredAgainst]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ScoredAgainst}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.scoredAgainst}</div>,
      align: "center",
    },
    [StandingColumns.Percentage]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.Percentage}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{roundNumber(value?.OVERALL?.percentage * 100)}</div>,
      align: "center",
    },
    [StandingColumns.Home]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Home}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL.winsHome - value?.OVERALL.lossesHome}</div>,
      align: "center",
    },
    [StandingColumns.Away]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Away}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL.winsAway - value?.OVERALL.lossesAway}</div>,
      align: "center",
    },
    [StandingColumns.Streak]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.Streak}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.streak}</div>,
      align: "center",
    },
    [StandingColumns.Div]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Div}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => value?.IN_DIVISION?.wins - value?.IN_DIVISION?.losses,
      align: "center",
    },
    [StandingColumns.Conf]: {
      title: formatMessage({ id: `standings.column.${StandingColumns.Conf}` }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => value?.IN_CONFERENCE?.wins - value?.IN_CONFERENCE?.losses,
      align: "center",
    },
    [StandingColumns.StandingPoints]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.StandingPoints}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.standingPoints}</div>,
      align: "center",
    },
    [StandingColumns.GamesBehind]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.GamesBehind}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.gamesBehind}</div>,
      align: "center",
    },
    [StandingColumns.ForfeitsGiven]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ForfeitsGiven}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.forfeitsGiven}</div>,
      align: "center",
    },
    [StandingColumns.ForfeitsWonBy]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ForfeitsWonBy}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.forfeitsWonBy}</div>,
      align: "center",
    },
    [StandingColumns.HighestScoreAgainst]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.HighestScoreAgainst}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.highestScoreAgainst}</div>,
      align: "center",
    },
    [StandingColumns.HighestScoreAgainstAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.HighestScoreAgainstAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.highestScoreAgainstAway}</div>,
      align: "center",
    },
    [StandingColumns.HighestScoreAgainstHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.HighestScoreAgainstHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.highestScoreAgainstHome}</div>,
      align: "center",
    },
    [StandingColumns.HighestScoreFor]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.HighestScoreFor}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.highestScoreFor}</div>,
      align: "center",
    },
    [StandingColumns.HighestScoreForAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.HighestScoreForAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.highestScoreForAway}</div>,
      align: "center",
    },
    [StandingColumns.HighestScoreForHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.HighestScoreForHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.highestScoreForHome}</div>,
      align: "center",
    },
    [StandingColumns.LossesAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LossesAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lossesAway}</div>,
      align: "center",
    },
    [StandingColumns.LossesHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LossesHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lossesHome}</div>,
      align: "center",
    },
    [StandingColumns.LowestScoreAgainst]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LowestScoreAgainst}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lowestScoreAgainst}</div>,
      align: "center",
    },
    [StandingColumns.LowestScoreAgainstAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LowestScoreAgainstAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lowestScoreAgainstAway}</div>,
      align: "center",
    },
    [StandingColumns.LowestScoreAgainstHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LowestScoreAgainstHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lowestScoreAgainstHome}</div>,
      align: "center",
    },
    [StandingColumns.LowestScoreFor]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LowestScoreFor}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lowestScoreFor}</div>,
      align: "center",
    },
    [StandingColumns.LowestScoreForAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LowestScoreForAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lowestScoreForAway}</div>,
      align: "center",
    },
    [StandingColumns.LowestScoreForHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.LowestScoreForHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.lowestScoreForHome}</div>,
      align: "center",
    },
    [StandingColumns.PercentageAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PercentageAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{roundNumber(value?.OVERALL?.percentageAway * 100)}</div>,
      align: "center",
    },
    [StandingColumns.PercentageHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PercentageHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{roundNumber(value?.OVERALL?.percentageHome * 100)}</div>,
      align: "center",
    },
    [StandingColumns.PlayedAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PlayedAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.playedAway}</div>,
      align: "center",
    },
    [StandingColumns.PlayedHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PlayedHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.playedHome}</div>,
      align: "center",
    },
    [StandingColumns.PointDifference]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PointDifference}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.pointDifference}</div>,
      align: "center",
    },
    [StandingColumns.PointDifferenceAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PointDifferenceAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.pointDifferenceAway}</div>,
      align: "center",
    },
    [StandingColumns.PointDifferenceHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PointDifferenceHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.pointDifferenceHome}</div>,
      align: "center",
    },
    [StandingColumns.ScoredAgainstAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ScoredAgainstAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.scoredAgainstAway}</div>,
      align: "center",
    },
    [StandingColumns.ScoredAgainstHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ScoredAgainstHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.scoredAgainstHome}</div>,
      align: "center",
    },
    [StandingColumns.ScoredForAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ScoredForAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.scoredForAway}</div>,
      align: "center",
    },
    [StandingColumns.ScoredForHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ScoredForHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.scoredForHome}</div>,
      align: "center",
    },
    [StandingColumns.StreakAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.StreakAway}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.streakAway}</div>,
      align: "center",
    },
    [StandingColumns.StreakHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.StreakHome}`,
      }),
      dataIndex: "calculated",
      width: 50,
      render: (value: StandingsCalculated) => <div>{value?.OVERALL?.streakHome}</div>,
      align: "center",
    },
    [StandingColumns.BonusStandingPoints]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.BonusStandingPoints}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.bonusStandingPoints}</div>,
      align: "center",
    },
    [StandingColumns.ByeStandingPoints]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ByeStandingPoints}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.byeStandingPoints}</div>,
      align: "center",
    },
    [StandingColumns.PenaltyStandingPoints]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.PenaltyStandingPoints}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.penaltyStandingPoints}</div>,
      align: "center",
    },
    [StandingColumns.StandingPointsAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.StandingPointsAway}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.standingPointsAway}</div>,
      align: "center",
    },
    [StandingColumns.StandingPointsGiven]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.StandingPointsGiven}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.standingPointsGiven}</div>,
      align: "center",
    },
    [StandingColumns.StandingPointsHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.StandingPointsHome}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.standingPointsHome}</div>,
      align: "center",
    },
    [StandingColumns.TeamDifferential]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.TeamDifferential}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.teamDifferential}</div>,
      align: "center",
    },
    [StandingColumns.TeamDifferentialAway]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.TeamDifferentialAway}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.teamDifferentialAway}</div>,
      align: "center",
    },
    [StandingColumns.TeamDifferentialHome]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.TeamDifferentialAway}`,
      }),
      dataIndex: "points",
      width: 50,
      render: (value: StandingsPoints) => <div>{value?.OVERALL?.teamDifferentialHome}</div>,
      align: "center",
    },
    [StandingColumns.ResultString]: {
      title: formatMessage({
        id: `standings.column.${StandingColumns.ResultString}`,
      }),
      dataIndex: "calculated",
      width: 105,
      render: (value: StandingsCalculated) => <>{composeResultString(value?.OVERALL?.resultString)}</>,
      align: "center",
    },
    actions: {
      width: 50,
      fixed: "right",
      render: (_: unknown, row: StandingTransformed) => <StandingsTableActions standingId={row.standingId} />,
    },
  };
  let orderCounter = 1;
  for (const key of Object.keys(columns)) {
    columns[key].order = orderCounter++;
  }
  return columns;
};
